import { isPlainObject } from 'remeda';
import z from 'zod';

import {
    decodeHeadingVariants,
    encodeHeadingVariants,
    PlateHeadingVariant,
    UnityHeadingVariant,
} from './HeadingVariants';
import { PlateTextElements, TextElementsCodec, UnityTextElements } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES } from '../constants';

export const UnityTitle = z.object({
    type: z.literal(ELEMENT_TYPES.TITLE),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    items: UnityTextElements,
    variants: z.record(z.string(), UnityHeadingVariant).optional(),
});
export type UnityTitle = z.infer<typeof UnityTitle>;

// A shallow type check on purpose: callers use it to peek at a raw backend payload, so it
// must not reject elements that the full schema would consider malformed.
export const isUnityTitle = (value: unknown): value is UnityTitle =>
    isPlainObject(value) && value.type === ELEMENT_TYPES.TITLE;

export const PlateTitle = z.object({
    type: z.literal(ELEMENT_TYPES.TITLE),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
    variants: z.record(z.string(), PlateHeadingVariant).optional(),
});
export type TitleElement = z.infer<typeof PlateTitle>;

export const TitleCodec = z.codec(UnityTitle, PlateTitle, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
        children: TextElementsCodec.decode(value.items),
        ...(value.variants ? { variants: decodeHeadingVariants(value.variants) } : {}),
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        items: TextElementsCodec.encode(value.children),
        ...value.data,
        ...(value.variants ? { variants: encodeHeadingVariants(value.variants) } : {}),
    }),
});
